import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import Parser from 'html-react-parser';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import { List } from 'react-virtualized';
import { I18n } from 'i18n-js';
import ActionButton from '../common/ActionButton';
import FlagIcon from '../common/FlagIcon';
import CountriesData from '../../shared/CountriesData';
import InterviewIcon from '../../images/org-registration/interviewIcon.svg';
import CrmIcon from '../../images/org-registration/crmIcon.svg';
import Dashboard from '../../images/org-registration/dashboardIcon.svg';
import EventAppIcon from '../../images/org-registration/eventAppIcon.svg';
import EventIcon from '../../images/org-registration/eventIcon.svg';
import SmsIcon from '../../images/org-registration/smsIcon.svg';
import {
  ICON_COLOR, RAKUNA_COLOR, LIGHT_GREY, DARK_GREY_TEXT,
} from '../../shared/Constants';
import translations from '../../locales.json';

const i18n = new I18n(translations);

const styles = theme => ({
  form: {
    flexGrow: 1,
    width: '100%', // Fix IE 11 issue.
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
  },
  select: {
    paddingBottom: theme.spacing(7 / 8),
  },
  typo: {
    fontSize: '1.4375rem',
    color: DARK_GREY_TEXT,
  },
  gridProduct: {
    border: '1px solid #39C2EE',
    borderRadius: '5px',
    marginBottom: theme.spacing(2),
    color: DARK_GREY_TEXT,
    fontSize: '1.0625rem',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
  },
  noteProduct: {
    fontSize: '0.9375rem',
    color: LIGHT_GREY,
  },
  icon: {
    width: '1.875rem',
    height: '1.875rem',
    fill: ICON_COLOR,
  },
  titleProduct: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  noteTypo: {
    fontSize: '0.9375rem',
    color: RAKUNA_COLOR,
  },
});

class OrganizationRegistrationForm extends Component {
  state = {
    formAttrs: {},
    childNodes: '',
    openingSelect: false,
  }

  componentDidMount = () => {
    const originalForm = document.getElementById('organization-sign-up-form');
    if (!originalForm) { return; }
    const formAttrs = ['action', 'acceptCharset', 'method'].reduce((obj, val) => {
      const transformObj = obj;
      transformObj[val] = originalForm[val];
      return transformObj;
    }, {});
    const childNodes = originalForm.innerHTML;
    this.setState({
      formAttrs,
      childNodes,
    });
  };

  render() {
    const countryList = Object.values(CountriesData);
    const {
      values: {
        title, website, contact, country, features,
      },
      errors,
      touched,
      handleChange,
      isValid,
      setFieldTouched,
      setFieldValue,
      classes,
    } = this.props;
    const {
      interview, event, crm, sms,
    } = window.gon.productFeatureIds;

    const change = (e, name) => {
      e.persist();
      handleChange(e);
      setFieldTouched(name, true, false);
    };

    const changeCountry = (e) => {
      e.persist();
      setFieldValue('country', e.target.getAttribute('code'));
      setFieldTouched('country', true, false);
      this.setState({ openingSelect: false });
    };

    const handleCheck = (e, name) => {
      const untouchedField = features.filter(f => name.indexOf(f.id) === -1);
      const touchedField = name.map(s => ({ id: s, enable: e.target.checked }));
      setFieldValue('features', [...untouchedField, ...touchedField]);
    };

    const isProductEnabled = (p) => {
      const enabledIds = features.filter(f => (p.indexOf(f.id) !== -1 && f.enable === true));
      return enabledIds.length === p.length;
    };

    const commonCrmSmsFeatures = crm.filter(fid => sms.indexOf(fid) !== -1);
    const handleChangeCrmSms = (e, changedProduct, otherProduct) => {
      let changedFeatures = [];
      let unchangedFeatures = [];
      if (isProductEnabled(otherProduct)) {
        const unCommonFeatures = changedProduct.filter(fid => (
          commonCrmSmsFeatures.indexOf(fid) === -1));
        unchangedFeatures = features.filter(f => unCommonFeatures.indexOf(f.id) === -1);
        changedFeatures = unCommonFeatures.map(s => ({ id: s, enable: e.target.checked }));
      } else {
        unchangedFeatures = features.filter(f => changedProduct.indexOf(f.id) === -1);
        changedFeatures = changedProduct.map(s => ({ id: s, enable: e.target.checked }));
      }
      setFieldValue('features', [...unchangedFeatures, ...changedFeatures]);
    };

    const productFields = features.reduce((prodFeatures, f, ind) => ([
      ...prodFeatures,
      { name: `organization[organizations_features_attributes][${ind}][feature_id]`, value: f.id },
      { name: `organization[organizations_features_attributes][${ind}][enable]`, value: f.enable },
    ]), []);

    const {
      state: { formAttrs, childNodes, openingSelect },
    } = this;

    const hiddenFields = [
      { name: 'organization[title]', value: title },
      { name: 'organization[website]', value: website },
      { name: 'organization[contact]', value: ['+', CountriesData[country].dialCode, contact].join('') },
      ...productFields,
    ];

    const renderFlagRow = (row) => {
      const { index, key, style } = row;
      const item = countryList[index];
      return (
        <FlagIcon
          showingName
          name="country"
          style={{ ...style }}
          country={item}
          key={key}
          code={item.iso2}
          onClick={changeCountry}
        />
      );
    };

    return (
      <form {...formAttrs} className={classes.form}>
        {Parser(childNodes)}
        {hiddenFields.map(field => (
          <input
            type="hidden"
            key={field.name}
            name={field.name}
            value={field.value}
          />
        ))}
        <Grid container direction="row" justifyContent="space-evenly" alignItems="center" spacing={1}>
          <Grid item xs={12}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="title">{i18n.t('js.sign_up.organization.registration.form.title')}</InputLabel>
              <Input
                id="title"
                value={title}
                error={touched.title && Boolean(errors.title)}
                onChange={e => change(e, 'title')}
              />
              <FormHelperText>{touched.title ? errors.title : ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container direction="row" justifyContent="space-evenly" alignItems="center" spacing={1}>
          <Grid item xs={12}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="website">{i18n.t('js.sign_up.organization.registration.form.website')}</InputLabel>
              <Input
                id="website"
                value={website}
                error={touched.website && Boolean(errors.website)}
                onChange={e => change(e, 'website')}
              />
              <FormHelperText>{touched.website ? errors.website : ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container direction="row" justifyContent="space-evenly" alignItems="flex-end" spacing={1}>
          <Grid item xs={5} sm={3} className={classes.select}>
            <FormControl margin="none" fullWidth>
              <Select
                variant="standard"
                value={country}
                renderValue={(value) => {
                  const item = CountriesData[value];
                  return (
                    <FlagIcon country={item} />
                  );
                }}
                open={openingSelect}
                onOpen={() => this.setState({ openingSelect: true })}
                onClose={() => this.setState({ openingSelect: false })}
              >
                <List
                  width={300}
                  height={500}
                  rowHeight={40}
                  rowCount={Object.keys(CountriesData).length}
                  rowRenderer={renderFlagRow}
                />
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={7} sm={9}>
            <FormControl margin="dense" required fullWidth>
              <InputLabel htmlFor="contact">{i18n.t('js.sign_up.organization.registration.form.contact')}</InputLabel>
              <Input
                id="contact"
                value={contact}
                error={touched.contact && Boolean(errors.contact)}
                type="tel"
                onChange={e => change(e, 'contact')}
              />
              <FormHelperText>{touched.contact ? errors.contact : ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container direction="column">
          <Grid item className={classes.titleProduct}>
            <Typography className={classes.typo}>{i18n.t('js.sign_up.organization.registration.form.products.title')}</Typography>
            <Typography className={classes.noteTypo}>{i18n.t('js.sign_up.organization.registration.form.products.choose')}</Typography>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" alignItems="center" justifyContent="space-evenly">
              <Grid item xs={2} md={1}>
                <InterviewIcon className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.interview.scheduling')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.interview.dsc')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(interview)}
                  onChange={e => handleCheck(e, interview)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              <Grid item xs={2} md={1}>
                <CrmIcon className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.crm.title')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.crm.note_product')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(crm)}
                  onChange={e => handleChangeCrmSms(e, crm, sms)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              <Grid item xs={2} md={1}>
                <SmsIcon className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.text_recruiting.title')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.text_recruiting.note_product')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(sms)}
                  onChange={e => handleChangeCrmSms(e, sms, crm)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Grid item xs={2} md={1}>
                <EventAppIcon className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.recruit_app.title')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.recruit_app.note_product')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(event)}
                  onChange={e => handleCheck(e, event)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              <Grid item xs={2} md={1}>
                <Dashboard className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.dashboard.title')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.dashboard.note_product')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(event)}
                  onChange={e => handleCheck(e, event)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.gridProduct}>
            <Grid container direction="row" justifyContent="space-evenly" alignItems="center">
              <Grid item xs={2} md={1}>
                <EventIcon className={classes.icon} />
              </Grid>
              <Grid item xs={8} md={10}>
                <Typography>{i18n.t('js.sign_up.organization.registration.form.event_management.title')}</Typography>
                <Typography className={classes.noteProduct}>
                  {i18n.t('js.sign_up.organization.registration.form.event_management.note_product')}
                </Typography>
              </Grid>
              <Grid item xs={2} md={1}>
                <Checkbox
                  checked={isProductEnabled(event)}
                  onChange={e => handleCheck(e, event)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item container justifyContent="center" direction="row">
            <Grid item xs={4}>
              <ActionButton disabled={!isValid} text={i18n.t('js.sign_up.organization.registration.form.submit')} type="submit" />
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }
}

OrganizationRegistrationForm.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  values: PropTypes.instanceOf(Object).isRequired,
  errors: PropTypes.instanceOf(Object).isRequired,
  touched: PropTypes.instanceOf(Object).isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
};

export default withStyles(styles)(OrganizationRegistrationForm);
